import { useState } from "react";
import { MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { useContent } from "@/content/ContentContext";

const inputClass =
  "w-full border border-foreground/15 bg-background px-4 py-3.5 text-sm text-foreground outline-none transition-colors duration-300 placeholder:text-muted-foreground/60 focus:border-primary";

export const Contact = ({ onOpenDatenschutz }) => {
  const content = useContent();
  const { contact } = content;
  const whatsappUrl = `https://wa.me/${contact.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(contact.whatsapp_text)}`;
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [privacy, setPrivacy] = useState(false);
  const [status, setStatus] = useState("idle");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) throw new Error("send failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="border-y border-foreground/10 bg-card"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-24 md:grid-cols-2 md:gap-12 md:px-10 md:py-40">
        <div>
          <Reveal>
            <p
              className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-primary"
              data-testid="contact-chapter"
            >
              04 · Kontakt
            </p>
            <h2
              className="max-w-xl font-heading text-4xl font-black leading-[1.02] tracking-tight text-foreground sm:text-5xl lg:text-6xl"
              data-testid="contact-heading"
            >
              {contact.heading}
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-8 max-w-md text-base text-muted-foreground md:text-lg">
              {contact.text}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-wrap items-center gap-6">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-call-button"
                className="group inline-flex items-center gap-3 rounded-full bg-primary px-8 py-5 font-heading text-base font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background md:text-lg"
              >
                <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
                {contact.phone}
              </a>
              <p
                className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground"
                data-testid="contact-availability"
              >
                {contact.availability}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-16 border-t border-foreground/10 pt-8">
              <p
                className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground md:text-sm"
                data-testid="contact-address"
              >
                {contact.address}
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="border border-foreground/10 bg-background p-6 md:p-8">
            {status === "success" ? (
              <div
                className="flex h-full min-h-[380px] flex-col items-start justify-center"
                data-testid="contact-form-success"
              >
                <CheckCircle2 className="mb-6 h-10 w-10 text-primary" />
                <h3 className="font-heading text-2xl font-black tracking-tight text-foreground md:text-3xl">
                  Angekommen!
                </h3>
                <p className="mt-4 max-w-sm text-sm text-muted-foreground md:text-base">
                  Danke für deine Anfrage. Ich melde mich so schnell wie
                  möglich bei dir – versprochen. Wenn's eilt: einfach
                  durchrufen.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="space-y-5"
              >
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                  Schreib mir kurz
                </p>
                <div>
                  <label
                    htmlFor="contact-name"
                    className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground"
                  >
                    Dein Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Wie heißt du?"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-contact"
                    className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground"
                  >
                    Telefon oder E-Mail
                  </label>
                  <input
                    id="contact-contact"
                    data-testid="contact-contact-input"
                    required
                    value={form.contact}
                    onChange={update("contact")}
                    placeholder="Wie erreiche ich dich?"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-message"
                    className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground"
                  >
                    Worum geht's?
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Z. B.: Wohnzimmer im Altbau verputzen, ca. 30 m² …"
                    className={`${inputClass} resize-none`}
                  />
                </div>
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-muted-foreground">
                  <input
                    type="checkbox"
                    data-testid="contact-privacy-checkbox"
                    required
                    checked={privacy}
                    onChange={(e) => setPrivacy(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(15,80%,54%)]"
                  />
                  <span>
                    Ich habe die{" "}
                    <button
                      type="button"
                      onClick={onOpenDatenschutz}
                      data-testid="contact-datenschutz-link"
                      className="underline underline-offset-2 transition-colors duration-300 hover:text-primary"
                    >
                      Datenschutzerklärung
                    </button>{" "}
                    gelesen und bin einverstanden, dass meine Angaben zur
                    Beantwortung der Anfrage verwendet werden.
                  </span>
                </label>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === "sending"}
                  className="group inline-flex w-full items-center justify-center gap-3 bg-primary px-8 py-4 font-heading text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  {status === "sending" ? "Wird gesendet …" : "Anfrage abschicken"}
                </button>
                {status === "error" && (
                  <p className="text-sm text-primary" data-testid="contact-form-error">
                    Das hat leider nicht geklappt. Versuch's noch mal – oder
                    ruf einfach direkt durch: +49 175 679825183
                  </p>
                )}
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
